import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import React, { ChangeEvent, Component, DragEvent, createRef } from 'react';

import { uploadExerciseVideo } from '../../api/exercises';
import ExerciseVideoOverwriteDialog from './ExerciseVideoOverwriteDialog';

interface Props {
    disabled?: boolean;
    onVideoUploaded: (videoUrl: string) => void;
    videoUrl?: string;
}

interface State {
    errorMessage?: string;
    isDragging: boolean;
    isUploading: boolean;
    pendingFile?: File;
    showOverrideDialog: boolean;
    uploadedFileName?: string;
}

class ExerciseVideoUploadDropzone extends Component<Props, State> {
    private fileInputRef = createRef<HTMLInputElement>();

    state: State = {
        errorMessage: undefined,
        isDragging: false,
        isUploading: false,
        pendingFile: undefined,
        showOverrideDialog: false,
        uploadedFileName: undefined,
    };

    onBrowseClick = () => {
        this.fileInputRef.current?.click();
    };

    onDragEnter = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (this.props.disabled) {
            return;
        }

        this.setState({ errorMessage: undefined, isDragging: true });
    };

    onDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        this.setState({ isDragging: false });
    };

    onDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    onDrop = async (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        this.setState({ isDragging: false });

        if (this.props.disabled) {
            return;
        }

        const droppedFile = event.dataTransfer.files?.[0];

        if (!droppedFile) {
            return;
        }

        await this.processSelectedFile(droppedFile);
    };

    onFileInputChanged = async (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0];

        if (!selectedFile) {
            return;
        }

        await this.processSelectedFile(selectedFile);
        event.target.value = '';
    };

    onOverrideCancel = () => {
        this.setState({ pendingFile: undefined, showOverrideDialog: false });
    };

    onOverrideConfirm = async () => {
        const { pendingFile } = this.state;

        if (!pendingFile) {
            return;
        }

        await this.uploadFile(pendingFile, true);
    };

    async processSelectedFile(file: File) {
        if (!file.type.startsWith('video/')) {
            this.setState({ errorMessage: 'Please choose a valid video file.' });
            return;
        }

        this.setState({ errorMessage: undefined, uploadedFileName: file.name });
        await this.uploadFile(file, false);
    }

    async uploadFile(file: File, overrideExistingFile: boolean) {
        this.setState({ errorMessage: undefined, isUploading: true, pendingFile: file, showOverrideDialog: false });

        try {
            const uploadResult = await uploadExerciseVideo(file, overrideExistingFile);

            this.props.onVideoUploaded(uploadResult.videoUrl);
            this.setState({
                errorMessage: undefined,
                isUploading: false,
                pendingFile: undefined,
                showOverrideDialog: false,
                uploadedFileName: uploadResult.fileName,
            });
        } catch (error: unknown) {
            const conflictError = error as Error & { conflict?: boolean };

            if (conflictError.conflict) {
                this.setState({ isUploading: false, pendingFile: file, showOverrideDialog: true });
                return;
            }

            this.setState({
                errorMessage: error instanceof Error ? error.message : 'Unable to upload the exercise video.',
                isUploading: false,
                pendingFile: undefined,
                showOverrideDialog: false,
            });
        }
    }

    render() {
        const { disabled, videoUrl } = this.props;
        const { errorMessage, isDragging, isUploading, pendingFile, showOverrideDialog, uploadedFileName } = this.state;

        return (
            <>
                <Paper
                    onDragEnter={this.onDragEnter}
                    onDragLeave={this.onDragLeave}
                    onDragOver={this.onDragOver}
                    onDrop={this.onDrop}
                    sx={{
                        backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.06)' : 'background.default',
                        border: '1px dashed',
                        borderColor: isDragging ? 'primary.main' : 'divider',
                        borderRadius: 3,
                        mt: 1,
                        p: 2,
                    }}
                    variant="outlined"
                >
                    <input accept="video/*" hidden onChange={this.onFileInputChanged} ref={this.fileInputRef} type="file" />
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, justifyContent: 'space-between' }}>
                        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
                            <CloudUploadRoundedIcon color={isDragging ? 'primary' : 'action'} />
                            <Box>
                                <Typography sx={{ fontWeight: 700 }} variant="body2">
                                    Drop a video here to upload to Firebase Storage
                                </Typography>
                                <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }} variant="body2">
                                    Stored under /exercises. Existing file names are blocked unless you explicitly override them.
                                </Typography>
                            </Box>
                        </Box>
                        <Button disabled={disabled || isUploading} onClick={this.onBrowseClick} size="small" variant="outlined">Choose File</Button>
                    </Box>
                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mt: 1.5 }}>
                        {isUploading ? <CircularProgress size={18} /> : <InsertDriveFileRoundedIcon color="action" fontSize="small" />}
                        <Typography color="text.secondary" sx={{ fontSize: '0.82rem' }} variant="body2">
                            {isUploading
                                ? `Uploading ${pendingFile?.name || 'video'}...`
                                : uploadedFileName
                                    ? `Last uploaded file: ${uploadedFileName}`
                                    : videoUrl
                                        ? 'A video URL is already set for this exercise.'
                                        : 'No video uploaded yet.'}
                        </Typography>
                    </Box>
                    {errorMessage && <Alert severity="error" sx={{ mt: 1.5 }}>{errorMessage}</Alert>}
                </Paper>
                <ExerciseVideoOverwriteDialog
                    fileName={pendingFile?.name}
                    onCancel={this.onOverrideCancel}
                    onConfirm={this.onOverrideConfirm}
                    saving={isUploading}
                    visible={showOverrideDialog}
                />
            </>
        );
    }
}

export default ExerciseVideoUploadDropzone;